import React from "react";
import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";

import { Bath, Bed, Clipboard } from "@/components/bnp-icons";
import { formatNumber } from "@/utils/formatNumber";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import { getDevelopmentType, getDevelopmentTypeName } from "../investment-card";
import { getPropertyType } from "@/components/pages/user/dashboard/properties/CreatePropertyForm";
import { useDisclosure } from "@mantine/hooks";
import { Modal } from "@mantine/core";
import { Button } from "@/components/bnp-ui";

interface ProjectDetailsProps {
  project: any;
  deletedProject: any;
  setOpenedProject: (project: any) => void;
  setDeletedProject: (project: any) => void;
  setDetailsView: (state: any) => void;
  isLoading: any;
  id: string;
  name: string;
  propertyType: string;
  projectType: string;
  listingType: string;
  projectEndDate: Date;
  investmentType?: string;
  updates?: string;
  bedroomCount?: number;
  bathroomCount?: number;
  minBudget: string;
  maxBudget: string;
  minimumReturns: string;
  address: string;
  city?: string;
  onDelete?: (id: string) => void; // Add delete handler
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  project,
  deletedProject,
  setDeletedProject,
  id,
  isLoading,
  projectEndDate,
  updates,
  name,
  propertyType,
  projectType,
  investmentType,
  listingType,
  bedroomCount,
  bathroomCount,
  minBudget,
  maxBudget,
  minimumReturns,
  address,
  city,
  onDelete,
  setDetailsView,
  setOpenedProject,
}) => {
  const [opened, { open, close }] = useDisclosure(false);

  const handleDelete = () => {
    setDeletedProject(project?.id);
    onDelete && onDelete(project?.id);
  };

  return (
    <>
      <div
        className={
          "relative lg:w-[360px] w-[100%] h-[345px] bg-bg-grey p-6 shadow-md bg-[url('/assets/images/city.png')] bg-no-repeat bg-right-bottom"
        }
      >
        <div className="flex justify-between mb-4">
          <div>
            <svg
              width="46"
              height="46"
              viewBox="0 0 46 46"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="1" y="1" width="44" height="44" rx="22" fill="white" />
              <rect
                x="1"
                y="1"
                width="44"
                height="44"
                rx="22"
                stroke="#E2E4E9"
              />
              <path
                d="M27.5004 14V15.8H30.2067C30.6999 15.8 31.1004 16.2005 31.1004 16.6937V31.1063C31.1004 31.5995 30.6999 32 30.2067 32H15.7941C15.3009 32 14.9004 31.5995 14.9004 31.1063V16.6937C14.9004 16.2005 15.3009 15.8 15.7941 15.8H18.5004V14H27.5004ZM18.5004 17.6H16.7004V30.2H29.3004V17.6H27.5004V19.4H18.5004V17.6ZM20.3004 26.6V28.4H18.5004V26.6H20.3004ZM20.3004 23.9V25.7H18.5004V23.9H20.3004ZM20.3004 21.2V23H18.5004V21.2H20.3004ZM25.7004 15.8H20.3004V17.6H25.7004V15.8Z"
                fill="#525866"
              />
            </svg>
            <h2
              onClick={() => {
                setOpenedProject(project);
                setDetailsView(true);
              }}
              className=" cursor-pointer text-xl font-medium mt-4 text-main-900 max-w-[250px] truncate underline hover:text-bnp-cyan transition-colors ease-linear delay-200"
            >
              {capitalizeFirstLetter(name)}
            </h2>
          </div>

          {/* Delete button */}
          <button
            onClick={open}
            className="h-8 w-8 rounded-full bg-white hover:bg-red-50 flex items-center justify-center text-red-500 transition-colors"
            aria-label="Delete project"
            title="Delete project"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 14C3.23478 14 3 13.7652 3 13.5V4H2.5C2.22386 4 2 3.77614 2 3.5C2 3.22386 2.22386 3 2.5 3H5.5V2.5C5.5 1.67157 6.17157 1 7 1H9C9.82843 1 10.5 1.67157 10.5 2.5V3H13.5C13.7761 3 14 3.22386 14 3.5C14 3.77614 13.7761 4 13.5 4H13V13.5C13 13.7652 12.7652 14 12.5 14H3.5ZM12 4H4V13H12V4ZM6.5 6C6.77614 6 7 6.22386 7 6.5V11.5C7 11.7761 6.77614 12 6.5 12C6.22386 12 6 11.7761 6 11.5V6.5C6 6.22386 6.22386 6 6.5 6ZM9.5 6C9.77614 6 10 6.22386 10 6.5V11.5C10 11.7761 9.77614 12 9.5 12C9.22386 12 9 11.7761 9 11.5V6.5C9 6.22386 9.22386 6 9.5 6ZM9.5 2.5C9.5 2.22386 9.27614 2 9 2H7C6.72386 2 6.5 2.22386 6.5 2.5V3H9.5V2.5Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        <div className="w-full flex items-center text-sm font-bold gap-4 text-main-900 mb-4 border-b border-soft-200 ">
          <div className="flex items-center gap-2">
            <Bed />
            <span>{bedroomCount ?? "-"}</span>
          </div>
          <div className="border-r border-grey-5 h-4"></div>
          <div className="flex items-center gap-2">
            <Bath />
            <span>{bathroomCount ?? "-"}</span>
          </div>
          <div className="border-r border-grey-5 h-4"></div>

          <div className="flex items-center" title={address}>
            <span className="max-w-[180px] truncate">{address}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 font-bold text-main-900">
          <span>
            NGN {formatNumber(minBudget)} - {formatNumber(maxBudget)}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-4 text-main-900">
          {projectType === "investment" ? (
            <span
              className="max-w-[300px] truncate"
              title={capitalizeFirstLetter(
                getDevelopmentTypeName(investmentType ?? "")?.toLowerCase() ??
                  "",
              )}
            >
              Investment type:{" "}
              <span className="font-medium text-bnp-cyan">
                {capitalizeFirstLetter(
                  getDevelopmentTypeName(investmentType ?? "")?.toLowerCase() ??
                    "",
                )}
              </span>
            </span>
          ) : (
            <span
              className="max-w-[300px] truncate"
              title={getPropertyType(propertyType)}
            >
              Property type:{" "}
              <span className="font-medium text-bnp-cyan">
                {getPropertyType(propertyType)}
              </span>
            </span>
          )}
        </div>

        {projectType === "investment" ? (
          <div className="flex items-center gap-2 text-main-900">
            <span>
              Minimum returns:{" "}
              <span className="font-medium text-bnp-cyan">
                {formatNumber(minimumReturns)}%
              </span>
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-main-900">
            <span>
              Listing type:{" "}
              <span className="font-medium text-bnp-cyan">
                {capitalizeFirstLetter(projectType)}
              </span>
            </span>
          </div>
        )}

        <div className="flex items-center px-4 py-2 absolute left-0 bottom-6 h-[36px] bg-base-white text-grey-7 text-sm w-[360px]">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 2.59844H13.4C13.5591 2.59844 13.7117 2.66165 13.8243 2.77417C13.9368 2.8867 14 3.03931 14 3.19844V12.7984C14 12.9576 13.9368 13.1102 13.8243 13.2227C13.7117 13.3352 13.5591 13.3984 13.4 13.3984H2.6C2.44087 13.3984 2.28826 13.3352 2.17574 13.2227C2.06321 13.1102 2 12.9576 2 12.7984V3.19844C2 3.03931 2.06321 2.8867 2.17574 2.77417C2.28826 2.66165 2.44087 2.59844 2.6 2.59844H5V1.39844H6.2V2.59844H9.8V1.39844H11V2.59844ZM9.8 3.79844H6.2V4.99844H5V3.79844H3.2V6.19844H12.8V3.79844H11V4.99844H9.8V3.79844ZM12.8 7.39844H3.2V12.1984H12.8V7.39844Z"
              fill="#525866"
            />
          </svg>

          <p className="ml-2">
            {" "}
            {safeMoment(projectEndDate)?.format("DD MMMM, YYYY") ?? "-"}
          </p>
        </div>
      </div>

      <Modal
        opened={opened}
        onClose={close}
        closeOnClickOutside={true}
        centered
        title={
          <div className="flex p-4 gap gap-4 items-center border-b border-soft-200 w-[100%] lg:w-[790px]">
            <Clipboard />
            <div className="flex flex-col gap-1">
              <h1 className="text-blue-primary font-medium text-lg">
                Delete Project ?
              </h1>
            </div>
          </div>
        }
        size={"sm"}
        radius={"lg"}
      >
        <p className="text-base">Are you sure?</p>
        <div className="w-[100%] flex gap-3 justify-center mt-4 pt-4">
          <Button
            className="bg-white ml-auto border border-border-1 text-bnp-dark text-sm font-medium w-[94px] h-[60px] rounded-lg"
            onClick={close}
          >
            No
          </Button>

          <Button
            className="bg-bnp-dark text-white text-sm font-medium w-[94px] h-[60px] rounded-lg"
            onClick={handleDelete}
            isLoading={isLoading}
          >
            Yes
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ProjectDetails;
